import React, { FC, useEffect, useState } from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import axios from "axios";
import { MyHRErrorPage } from "my-hr/pages/error-page/error-page";
import { OcErrorBoundary, OcPrivateRoute } from "oc/components";
import { OcScrollToTop } from "oc/components/scroll-to-top/scroll-to-top";
import {
  useOcApi,
  useOcCustomerLicences,
  useOcGenerateRoutes,
  useOcInitStates,
  useOcLoginState,
} from "oc/hooks";
import { OcPublicLayout } from "oc/layouts/oc-public-layout/oc-public-layout";
import { OcPrivateLayout } from "oc/layouts/private-layout";
import { RouteDef } from "oc/types";

let interval: number;

export const App: FC = () => {
  const { onLogin, onLogout, isLoggedIn } = useOcLoginState();
  const { apiUrl } = useOcApi();
  const [errorState, setErrorState] = useState<boolean>(false);
  const [errorStateDb, setErrorStateDb] = useState<boolean>(false);
  const [errorStateType, setErrorStateType] = useState<string>("");

  useOcCustomerLicences();
  useOcInitStates();

  useEffect(() => {
    // The browser-reported online flag short-circuits both checks when the
    // OS knows the network is down (laptop sleep, VPN dropped, WiFi roam).
    // Without it the 5s interval keeps firing `axios.get` calls that all
    // time out and flood the console (and burn battery on mobile).
    const checkDb = (): void => {
      if (!document.hidden && navigator.onLine) {
        axios
          .get(`${apiUrl}/read-db-test`)
          .then(() => {
            setErrorStateDb(false);
            setErrorStateType("");
          })
          .catch(() => {
            setErrorStateDb(true);
            setErrorStateType("db");
          });
      }
    };

    const check = (): void => {
      if (!document.hidden && navigator.onLine) {
        axios
          .get(apiUrl)
          .then(() => {
            setErrorState(false);
            setErrorStateType("");
          })
          .catch(() => {
            setErrorState(true);
            setErrorStateType("net");
          });
      }
    };

    interval = window.setInterval(() => {
      check();
      checkDb();
    }, 5000);
    check();
    checkDb();

    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const routeDefs = useOcGenerateRoutes() as RouteDef[];

  if (errorState || errorStateDb) {
    return (
      <MyHRErrorPage
        errorStateDb={errorStateDb}
        errorState={errorState}
        errorStateType={errorStateType}
        apiUrl={apiUrl}
      />
    );
  }

  return (
    <>
      <OcScrollToTop>
        <OcPrivateLayout isLoggedIn={isLoggedIn} onLogout={onLogout}>
          <OcErrorBoundary>
            <Switch>
              {routeDefs?.map((item: RouteDef) => (
                <Route key={item.id} path={item.path} exact={item.exact}>
                  {item.private ? (
                    <OcPrivateRoute
                      path={item.path}
                      isLoggedIn={isLoggedIn}
                      roles={item.roles}
                    >
                      {React.createElement(item.component)}
                    </OcPrivateRoute>
                  ) : (
                    <OcPublicLayout>
                      {React.createElement(item.component, {
                        onLogin,
                        onLogout,
                      })}
                    </OcPublicLayout>
                  )}
                </Route>
              ))}
              <Redirect to="/404" />
            </Switch>
          </OcErrorBoundary>
        </OcPrivateLayout>
      </OcScrollToTop>
    </>
  );
};

export default App;
